import React from 'react';
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormErrorMessage,
} from '@chakra-ui/core';
import { Column } from 'react-table';
import isEqual from 'lodash/isEqual';
import Joi from 'joi';
import { DialogFlowAgent } from '../accountParameter.types';
import { DataTable } from './DataTable';
import { RiAddLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { useSettingsDisabled } from '../SettingsDisabledContext';

const agentSchema = Joi.object({
  displayName: Joi.string().trim().required(),
  agentId: Joi.string().trim().required(),
  defaultLanguageCode: Joi.string().trim().required(),
  location: Joi.string().trim().required(),
  timeZone: Joi.string().trim().required(),
});

export const DialogFlowAgentsManager: React.FC<{
  value: DialogFlowAgent[];
  onChange: (agents: DialogFlowAgent[]) => void;
  baselineAgents: DialogFlowAgent[];
}> = ({ value, onChange, baselineAgents }) => {
  const { t } = useTranslation('accountParameter');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [agentInForm, setAgentInForm] = React.useState<DialogFlowAgent | null>(null);
  const [errors, setErrors] = React.useState<Partial<Record<keyof DialogFlowAgent, boolean>>>({});
  const disabled = useSettingsDisabled();

  const handleOpenModalToAdd = () => {
    setAgentInForm({ displayName: '', agentId: '', defaultLanguageCode: '', location: '', timeZone: '' });
    setErrors({}); // Clear errors when opening modal
    setIsModalOpen(true);
  };

  const handleOpenModalToEdit = (agent: DialogFlowAgent) => {
    setAgentInForm(agent);
    setErrors({}); // Clear errors when opening modal
    setIsModalOpen(true);
  };

  const handleSaveAgent = () => {
    if (!agentInForm) return;

    const { error } = agentSchema.validate(agentInForm, { abortEarly: false });

    if (error) {
      const newErrors = error.details.reduce((acc, detail) => {
        const key = detail.path[0] as keyof DialogFlowAgent;
        acc[key] = true;
        return acc;
      }, {} as Partial<Record<keyof DialogFlowAgent, boolean>>);
      setErrors(newErrors);
      return;
    }

    setErrors({}); // Clear errors on successful validation

    const isEditing = value.some((a) => a.agentId === agentInForm.agentId);

    let updatedAgents: DialogFlowAgent[];
    if (isEditing) {
      updatedAgents = value.map((agent) => (agent.agentId === agentInForm.agentId ? agentInForm : agent));
    } else {
      updatedAgents = [...value, agentInForm];
    }

    onChange(updatedAgents);
    setIsModalOpen(false);
    setAgentInForm(null);
  };

  const handleRemoveAgent = (agentId: string) => {
    const updatedAgents = value.filter((agent) => agent.agentId !== agentId);
    onChange(updatedAgents);
  };

  const columns = React.useMemo<Column<DialogFlowAgent>[]>(
    () => [
      {
        Header: t('dialogFlowAgentsManager.displayName') as string,
        accessor: 'displayName',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('dialogFlowAgentsManager.agentId') as string,
        accessor: 'agentId',
        width: 300, // Make it double bigger
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('dialogFlowAgentsManager.language') as string,
        accessor: 'defaultLanguageCode',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('dialogFlowAgentsManager.location') as string,
        accessor: 'location',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('dialogFlowAgentsManager.timezone') as string,
        accessor: 'timeZone',
        width: 150,
        Cell: ({ value }) => (
          <Box whiteSpace="normal" wordBreak="break-all">
            {value}
          </Box>
        ),
      },
      {
        Header: t('analyticsPagesManager.actions') as string,
        id: 'actions',
        disableSortBy: true,
        width: 150, // Adjust width for actions column
        Cell: ({ row }: any) => (
          <Flex>
            <Button size="sm" mr={2} onClick={() => handleOpenModalToEdit(row.original)} disabled={disabled}>
              {t('analyticsPagesManager.edit')}
            </Button>
            <Button size="sm" colorScheme="red" onClick={() => handleRemoveAgent(row.original.agentId)} disabled={disabled}>
              {t('analyticsPagesManager.remove')}
            </Button>
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange, t],
  );

  const isSaveDisabled = !agentInForm;

  return (
    <Box p={4} mt={4} borderWidth="1px" borderRadius="md">
      <Flex justify="space-between" align="center" mb={4}>
        <Heading as="h5" size="sm">
          {t('dialogFlowAgentsManager.dialogflowAgents')}
        </Heading>
        <Button
          colorScheme="blue"
          variant="outline"
          onClick={handleOpenModalToAdd}
          leftIcon={<RiAddLine size="24px" />}
          disabled={disabled}
        >
          {t('dialogFlowAgentsManager.addAgent')}
        </Button>
      </Flex>
      <DataTable
        columns={columns}
        data={value}
        getRowProps={(row: any) => {
          const current = row.original as DialogFlowAgent;
          const baseline = baselineAgents.find((a) => a.agentId === current.agentId);
          const changed = !baseline || !isEqual(baseline, current);
          return { className: changed ? 'dirty' : '' };
        }}
      />
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {agentInForm && value.some((a) => a.agentId === agentInForm.agentId)
              ? t('dialogFlowAgentsManager.editDialogflowAgent')
              : t('dialogFlowAgentsManager.addNewDialogflowAgent')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {agentInForm && (
              <>
                <FormControl isInvalid={!!errors.displayName} isRequired>
                  <FormLabel>{t('dialogFlowAgentsManager.displayName')}</FormLabel>
                  <Input
                    value={agentInForm.displayName}
                    onChange={(e) => {
                      setAgentInForm({ ...agentInForm, displayName: e.target.value });
                      if (errors.displayName) setErrors((prevErrors) => ({ ...prevErrors, displayName: false }));
                    }}
                  />
                  <FormErrorMessage>{t('genericSettingsPane.fieldIsRequired')}</FormErrorMessage>
                </FormControl>
                <FormControl mt={4} isInvalid={!!errors.agentId} isRequired>
                  <FormLabel>{t('dialogFlowAgentsManager.agentId')}</FormLabel>
                  <Input
                    value={agentInForm.agentId}
                    onChange={(e) => {
                      setAgentInForm({ ...agentInForm, agentId: e.target.value });
                      if (errors.agentId) setErrors((prevErrors) => ({ ...prevErrors, agentId: false }));
                    }}
                  />
                  <FormErrorMessage>{t('genericSettingsPane.fieldIsRequired')}</FormErrorMessage>
                </FormControl>
                <FormControl mt={4} isInvalid={!!errors.defaultLanguageCode} isRequired>
                  <FormLabel>{t('dialogFlowAgentsManager.defaultLanguageCode')}</FormLabel>
                  <Input
                    value={agentInForm.defaultLanguageCode}
                    onChange={(e) => {
                      setAgentInForm({ ...agentInForm, defaultLanguageCode: e.target.value });
                      if (errors.defaultLanguageCode) setErrors((prevErrors) => ({ ...prevErrors, defaultLanguageCode: false }));
                    }}
                  />
                  <FormErrorMessage>{t('genericSettingsPane.fieldIsRequired')}</FormErrorMessage>
                </FormControl>
                <FormControl mt={4} isInvalid={!!errors.location} isRequired>
                  <FormLabel>{t('dialogFlowAgentsManager.location')}</FormLabel>
                  <Input
                    required
                    value={agentInForm.location}
                    onChange={(e) => {
                      setAgentInForm({ ...agentInForm, location: e.target.value });
                      if (errors.location) setErrors((prevErrors) => ({ ...prevErrors, location: false }));
                    }}
                  />
                  <FormErrorMessage>{t('genericSettingsPane.fieldIsRequired')}</FormErrorMessage>
                </FormControl>
                <FormControl mt={4} isInvalid={!!errors.timeZone} isRequired>
                  <FormLabel>{t('dialogFlowAgentsManager.timeZone')}</FormLabel>
                  <Input
                    value={agentInForm.timeZone}
                    onChange={(e) => {
                      setAgentInForm({ ...agentInForm, timeZone: e.target.value });
                      if (errors.timeZone) setErrors((prevErrors) => ({ ...prevErrors, timeZone: false }));
                    }}
                  />
                  <FormErrorMessage>{t('genericSettingsPane.fieldIsRequired')}</FormErrorMessage>
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              {t('dialogFlowAgentsManager.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleSaveAgent} isDisabled={isSaveDisabled}>
              {agentInForm && value.some((a) => a.agentId === agentInForm.agentId)
                ? t('dialogFlowAgentsManager.saveChanges')
                : t('dialogFlowAgentsManager.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
